export const authenticationProviderKinds = ["password", "google", "apple"] as const;

export type AuthenticationProviderKind = (typeof authenticationProviderKinds)[number];

export const authenticationErrorCodes = [
  "invalid_credentials",
  "invalid_email",
  "user_disabled",
  "too_many_requests",
  "network_failure",
  "provider_cancelled",
  "provider_configuration_unavailable",
  "credential_already_in_use",
  "missing_token",
  "invalid_token",
  "unknown",
] as const;

export type AuthenticationErrorCode = (typeof authenticationErrorCodes)[number];

export type AuthenticationUser = {
  uid: string;
  email: string | undefined;
  emailVerified: boolean;
  providerIds: string[];
};

export class AuthenticationError extends Error {
  constructor(
    public readonly code: AuthenticationErrorCode,
    message = authenticationErrorMessage(code),
  ) {
    super(message);
    this.name = "AuthenticationError";
  }
}

const firebaseCodeMap: Record<string, AuthenticationErrorCode> = {
  "auth/account-exists-with-different-credential": "credential_already_in_use",
  "auth/admin-restricted-operation": "provider_configuration_unavailable",
  "auth/cancelled-popup-request": "provider_cancelled",
  "auth/credential-already-in-use": "credential_already_in_use",
  "auth/email-already-in-use": "credential_already_in_use",
  "auth/invalid-credential": "invalid_credentials",
  "auth/invalid-email": "invalid_email",
  "auth/network-request-failed": "network_failure",
  "auth/operation-not-allowed": "provider_configuration_unavailable",
  "auth/popup-closed-by-user": "provider_cancelled",
  "auth/too-many-requests": "too_many_requests",
  "auth/unauthorized-domain": "provider_configuration_unavailable",
  "auth/user-disabled": "user_disabled",
  "auth/user-not-found": "invalid_credentials",
  "auth/wrong-password": "invalid_credentials",
  "credential-already-in-use": "credential_already_in_use",
  "invalid-credential": "invalid_credentials",
  "invalid-email": "invalid_email",
  "network-request-failed": "network_failure",
  "operation-not-allowed": "provider_configuration_unavailable",
  "popup-closed-by-user": "provider_cancelled",
  "too-many-requests": "too_many_requests",
  "unauthorized-domain": "provider_configuration_unavailable",
  "user-disabled": "user_disabled",
  "user-not-found": "invalid_credentials",
  "wrong-password": "invalid_credentials",
};

export function normalizeAuthenticationErrorCode(error: unknown): AuthenticationErrorCode {
  if (error instanceof AuthenticationError) {
    return error.code;
  }

  if (typeof error === "string") {
    return firebaseCodeMap[error] ?? "unknown";
  }

  if (isRecord(error)) {
    const code = error.code;
    if (typeof code === "string") {
      return firebaseCodeMap[code] ?? "unknown";
    }
  }

  return "unknown";
}

export function authenticationErrorMessage(code: AuthenticationErrorCode): string {
  switch (code) {
    case "invalid_credentials":
      return "The email or password was not recognized.";
    case "invalid_email":
      return "Enter a valid email address.";
    case "user_disabled":
      return "This account is disabled. Contact support for help.";
    case "too_many_requests":
      return "Too many attempts. Wait a moment and try again.";
    case "network_failure":
      return "Authentication could not reach the network. Try again.";
    case "provider_cancelled":
      return "Sign-in was cancelled.";
    case "provider_configuration_unavailable":
      return "This sign-in provider is not configured for this environment.";
    case "credential_already_in_use":
      return "This credential is already connected to another account.";
    case "missing_token":
      return "Authentication is required.";
    case "invalid_token":
      return "The authentication session is invalid or expired.";
    case "unknown":
      return "Authentication failed. Try again.";
  }
}

export function assertKnownAuthenticationProvider(
  provider: string,
): asserts provider is AuthenticationProviderKind {
  if (!authenticationProviderKinds.includes(provider as AuthenticationProviderKind)) {
    throw new AuthenticationError("provider_configuration_unavailable");
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
